import { Field, Input, Select, Textarea } from '@/components/ui';
import { copy, useI18n } from '@/lib/i18n';
import './BillingProfileFields.css';

export interface BillingProfile {
  countryCode: string;
  legalName: string;
  taxOffice: string | null;
  taxOrRegistrationNumber: string;
  identityNumber: string | null;
  address: string;
  city: string | null;
  postalCode: string | null;
}

export const emptyBillingProfile: BillingProfile = {
  countryCode: 'TR', legalName: '', taxOffice: '', taxOrRegistrationNumber: '',
  identityNumber: '', address: '', city: '', postalCode: '',
};

export function BillingProfileFields({ value, onChange, errors = {} }: {
  value: BillingProfile;
  onChange: (value: BillingProfile) => void;
  errors?: Record<string, string>;
}) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const countries = [['TR', 'Türkiye'], ['DE', c('Almanya', 'Germany')], ['GB', c('Birleşik Krallık', 'United Kingdom')], ['US', c('Amerika Birleşik Devletleri', 'United States')]];
  const otherCountry = !countries.some(([code]) => code === value.countryCode);
  return <div className="billing-fields">
    <div className="stack stack--sm">
      <Field label={c('Ülke', 'Country')} required error={otherCountry ? undefined : errors.countryCode}>
        {({ id }) => <Select id={id} value={otherCountry ? 'OTHER' : value.countryCode} onChange={event => onChange({ ...value, countryCode: event.target.value === 'OTHER' ? '' : event.target.value })}>
          {countries.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          <option value="OTHER">{c('Diğer ülke', 'Other country')}</option>
        </Select>}
      </Field>
      {otherCountry && <Field label={c('İki harfli ülke kodu', 'Two-letter country code')} required error={errors.countryCode} hint={c('Örneğin Fransa için FR, Hollanda için NL.', 'For example, FR for France or NL for the Netherlands.')}>
        {({ id, invalid }) => <Input id={id} invalid={invalid} value={value.countryCode} required minLength={2} maxLength={2} pattern="[A-Za-z]{2}" placeholder="FR" onChange={event => onChange({ ...value, countryCode: event.target.value.toUpperCase() })} />}
      </Field>}
    </div>
    <Field label={c('Yasal kurum adı', 'Legal organization name')} required error={errors.legalName}>
      {({ id, invalid }) => <Input id={id} invalid={invalid} autoComplete="organization" required minLength={2} maxLength={200} value={value.legalName} onChange={event => onChange({ ...value, legalName: event.target.value })} />}
    </Field>
    {value.countryCode === 'TR' && <Field label={c('Vergi dairesi', 'Tax office')} required error={errors.taxOffice}>
      {({ id, invalid }) => <Input id={id} invalid={invalid} required maxLength={120} value={value.taxOffice ?? ''} onChange={event => onChange({ ...value, taxOffice: event.target.value })} />}
    </Field>}
    <Field label={value.countryCode === 'TR' ? c('Vergi / kimlik numarası', 'Tax / identity number') : c('Şirket kayıt numarası', 'Company registration number')} required error={errors.taxOrRegistrationNumber}>
      {({ id, invalid }) => <Input id={id} invalid={invalid} required minLength={3} maxLength={80} value={value.taxOrRegistrationNumber} onChange={event => onChange({ ...value, taxOrRegistrationNumber: event.target.value })} />}
    </Field>
    <div className="billing-fields__wide"><Field label={c('Fatura adresi', 'Billing address')} required error={errors.address}>
      {({ id }) => <Textarea id={id} autoComplete="street-address" required rows={3} minLength={5} maxLength={1000} value={value.address} onChange={event => onChange({ ...value, address: event.target.value })} />}
    </Field></div>
    <Field label={c('Şehir', 'City')} error={errors.city}>
      {({ id, invalid }) => <Input id={id} invalid={invalid} autoComplete="address-level2" maxLength={120} value={value.city ?? ''} onChange={event => onChange({ ...value, city: event.target.value })} />}
    </Field>
    <Field label={c('Posta kodu', 'Postal code')} error={errors.postalCode}>
      {({ id, invalid }) => <Input id={id} invalid={invalid} autoComplete="postal-code" maxLength={24} value={value.postalCode ?? ''} onChange={event => onChange({ ...value, postalCode: event.target.value })} />}
    </Field>
  </div>;
}
